import { useCallback, useEffect, useRef, useState } from 'react';
import { useCheckSavedServiceExistence } from '../../../../services/business/savedService';
import { filterUndefined } from '../../../../utility/helpers/commonFunction';

const useCheckExistService = () => {
    const [existingServiceData, setExistingServiceData] = useState(null);
    const [shopServiceReferenceError, setShopServiceReferenceError] = useState(false);
    const debounceTimeoutRef = useRef(null);
    const previousValuesRef = useRef({
        selectedSavedServiceId: null,
        selectedService: null,
        selectedMakes: null,
        shopServiceReference: null,
    });

    // TanStack Query mutation for checking service existence
    const checkSavedServiceExistenceMutation = useCheckSavedServiceExistence({
        onSuccess: (data) => {
            console.log('Service existence check success:', data);
            setExistingServiceData(data);
            // Check for shop service reference conflict
            setShopServiceReferenceError(data?.shopServiceReferenceExist || false);
        },
        onError: (error) => {
            console.error('Error checking service existence:', error);
            setExistingServiceData(null);
            setShopServiceReferenceError(false);
        },
    });

    // Helper function to check if values have actually changed
    const hasValuesChanged = useCallback((selectedSavedServiceId, selectedService, selectedMakes, shopServiceReference) => {
        const prev = previousValuesRef.current;

        // Check if service ID changed
        if (prev.selectedSavedServiceId !== selectedSavedServiceId) return true;

        // Check if service changed
        if (prev.selectedService?._id !== selectedService?._id) return true;

        // Check if vehicle makes changed
        if (!prev.selectedMakes || !selectedMakes) {
            if (prev.selectedMakes !== selectedMakes) return true;
        } else {
            const prevMakeIds = prev.selectedMakes.map((make) => make._id).sort();
            const currentMakeIds = selectedMakes.map((make) => make._id).sort();
            if (JSON.stringify(prevMakeIds) !== JSON.stringify(currentMakeIds)) return true;
        }

        // Check if shop service reference changed
        if (prev.shopServiceReference !== shopServiceReference) return true;

        return false;
    }, []);

    const checkServiceExistence = useCallback(
        async (selectedSavedServiceId, selectedService, selectedMakes, shopServiceReference = '', variant = false) => {
            // Check if values have actually changed (skip for variant calls)
            if (!variant && !hasValuesChanged(selectedSavedServiceId, selectedService, selectedMakes, shopServiceReference)) {
                return; // No change, skip API call
            }

            // Update previous values
            previousValuesRef.current = {
                selectedSavedServiceId,
                selectedService,
                selectedMakes,
                shopServiceReference,
            };

            // If variant is true, don't use debounce - call immediately
            if (variant) {
                // Prepare vehicle makes payload
                let vehicleMakePayload;
                if (selectedMakes.some((make) => make._id === 'all')) {
                    vehicleMakePayload = ['all'];
                } else {
                    vehicleMakePayload = selectedMakes.map((make) => make._id);
                }

                const payload = {
                    _id: selectedSavedServiceId || undefined,
                    service: selectedService?.serviceSubCategory?._id || '',
                    vehicleMake: vehicleMakePayload,
                    shopServiceReference: shopServiceReference || undefined,
                    variant: true, // Add variant parameter
                };

                try {
                    const response = await checkSavedServiceExistenceMutation.mutateAsync(filterUndefined(payload));
                    return response; // Return response for immediate use
                } catch (error) {
                    console.error('Error checking service existence:', error);
                    return null;
                }
            }

            // Normal debounced flow for regular existence check
            // Clear any existing timeout
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }

            // Validate required fields
            if (!selectedService?._id || !selectedMakes || selectedMakes.length === 0) {
                setExistingServiceData(null);
                return;
            }

            // Debounce the API call
            debounceTimeoutRef.current = setTimeout(async () => {
                // Prepare vehicle makes payload
                let vehicleMakePayload;
                if (selectedMakes.some((make) => make._id === 'all')) {
                    vehicleMakePayload = ['all'];
                } else {
                    vehicleMakePayload = selectedMakes.map((make) => make._id);
                }

                const payload = {
                    _id: selectedSavedServiceId || undefined,
                    service: selectedService?.serviceSubCategory?._id || '',
                    vehicleMake: vehicleMakePayload,
                    shopServiceReference: shopServiceReference || undefined,
                };

                console.log('Checking service existence with payload:', payload);

                try {
                    await checkSavedServiceExistenceMutation.mutateAsync(filterUndefined(payload));
                } catch (error) {
                    console.error('Error checking service existence:', error);
                    setExistingServiceData(null);
                }
            }, 600); // 600ms debounce delay
        },
        [checkSavedServiceExistenceMutation]
    );

    const resetExistenceCheck = useCallback(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        setExistingServiceData(null);
        setShopServiceReferenceError(false);
        // Reset previous values to allow fresh checks
        previousValuesRef.current = {
            selectedSavedServiceId: null,
            selectedService: null,
            selectedMakes: null,
            shopServiceReference: null,
        };
    }, []);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    return {
        existingServiceData,
        shopServiceReferenceError,
        isCheckingExistence: checkSavedServiceExistenceMutation.isPending,
        checkServiceExistence,
        resetExistenceCheck,
    };
};

export default useCheckExistService;
